/* global console, window, sessionStorage, Office */

/**
 * Minimal crash capture for the task pane.
 *
 * Why this exists: when the add-in broke for a customer we had nothing. The
 * pane went blank, the error existed only in a WebView console the customer
 * could not open, and support had to guess. Office task panes have no visible
 * console, no dev tools by default, and no logs we can read afterwards, so an
 * error that is not deliberately captured is simply lost.
 *
 * This keeps the last N errors in memory and in sessionStorage so a customer
 * can read one back to support, and forwards them to a sink if one is
 * configured. It deliberately has no third-party dependency and never sends
 * anything on its own.
 *
 * PRIVACY: this runs inside a legal product. Never put document text, chat
 * content, tokens, or anything a user typed into an error report. Only the
 * error itself and the technical context below.
 */

import { redactString, redactDeep } from "./redact";

const MAX_STORED_ERRORS = 20;
const STORAGE_KEY = "aila_error_log";

export interface ErrorContext {
  /** Where it happened, e.g. "research-chat", "review-tiles". */
  area?: string;
  componentStack?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface CapturedError {
  message: string;
  stack?: string;
  area?: string;
  componentStack?: string;
  at: string;
  platform?: string;
  host?: string;
  addinVersion?: string;
  context?: ErrorContext;
}

type ErrorSink = (error: CapturedError) => void;

const sinks = new Map<string, ErrorSink>();
const recent: CapturedError[] = [];

/**
 * Register a destination for captured errors, e.g. our own backend or Sentry.
 * Keyed so a destination can be replaced or removed later — Sentry in
 * particular must be removable the moment a user turns out to be in Australian
 * mode. With no sinks registered, errors are kept locally only.
 */
export function addErrorSink(key: string, sink: ErrorSink): void {
  sinks.set(key, sink);
}

/** Remove a destination. Used to stop sending to Sentry. */
export function removeErrorSink(key: string): void {
  sinks.delete(key);
}

function officeContext(): Pick<CapturedError, "platform" | "host"> {
  try {
    // Office may not be ready, or may not exist at all (tests, browser preview).
    const ctx = typeof Office !== "undefined" ? Office.context : undefined;
    return {
      platform: ctx?.platform != null ? String(ctx.platform) : undefined,
      host: ctx?.host != null ? String(ctx.host) : undefined,
    };
  } catch {
    return {};
  }
}

/** Copy the caller's context and strip anything sensitive out of the copy. */
function sanitiseContext(context: ErrorContext): ErrorContext {
  const copy: ErrorContext = { ...context };
  redactDeep(copy);
  return copy;
}

/**
 * Reload errors kept from earlier in this pane's life.
 *
 * Office recycles the task pane WebView (idle, document switch, restart), and a
 * crash often IS the reload. Without reading these back, "Copy diagnostics"
 * reports nothing precisely when the customer needs it — which was the whole
 * problem this is meant to solve.
 */
function restore(): void {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored == null) return;
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return;
    for (const entry of parsed.slice(-MAX_STORED_ERRORS)) {
      if (entry != null && typeof entry === "object" && "message" in entry) {
        recent.push(entry as CapturedError);
      }
    }
  } catch {
    // Corrupt or unavailable storage is not worth failing over.
  }
}

restore();

function persist(entry: CapturedError): void {
  try {
    recent.push(entry);
    if (recent.length > MAX_STORED_ERRORS) recent.shift();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(recent));
  } catch {
    // Storage can be unavailable or full. Losing the log must never itself throw.
  }
}

/**
 * Record an error. Safe to call from anywhere, including inside a failing
 * render — it never throws.
 */
/**
 * Called when the pane is closing, so a destination can send what it has.
 * Registered rather than imported to keep this module free of dependencies on
 * the things that consume it.
 */
let onUnloadFlush: (() => void) | null = null;

/** Register a callback to run when the task pane is being torn down. */
export function setUnloadFlush(flush: (() => void) | null): void {
  onUnloadFlush = flush;
}

let reporting = false;

export function reportError(error: unknown, context?: ErrorContext): void {
  // A sink that throws or itself reports would otherwise recurse without bound.
  // No current sink does, but a future one must not be able to take the pane
  // down through the very thing meant to diagnose it.
  if (reporting) return;
  reporting = true;
  try {
    const err = error instanceof Error ? error : new Error(String(error));

    // Sanitise ONCE, here, before the entry is stored, logged, copied to the
    // clipboard, or sent anywhere. An error message or stack can carry a bearer
    // token or a fragment of the customer's document, and every destination
    // downstream of this point is a way for it to leave.
    const entry: CapturedError = {
      message: redactString(err.message),
      stack: err.stack != null ? redactString(err.stack) : undefined,
      area: context?.area,
      componentStack:
        context?.componentStack != null ? redactString(context.componentStack) : undefined,
      at: new Date().toISOString(),
      addinVersion: process.env.APP_VERSION,
      ...officeContext(),
      context: context != null ? sanitiseContext(context) : undefined,
    };

    persist(entry);
    console.error("[aila] captured error:", entry.message, entry);
    for (const sink of sinks.values()) {
      try {
        sink(entry);
      } catch {
        // One broken destination must not stop the others.
      }
    }
  } catch {
    // Never let error reporting become the error.
  } finally {
    reporting = false;
  }
}

/** Everything captured this session — for a "copy diagnostics" button. */
export function getCapturedErrors(): CapturedError[] {
  return [...recent];
}

/** Human-readable dump a customer can paste to support. */
export function formatCapturedErrors(): string {
  if (recent.length === 0) return "No errors recorded this session.";
  return recent
    .map((e, i) => {
      const where = e.area != null ? ` [${e.area}]` : "";
      return `#${i + 1} ${e.at}${where}\n${e.message}\n${e.stack ?? ""}`.trim();
    })
    .join("\n\n---\n\n");
}

/**
 * Catch the two things a React error boundary cannot: errors thrown outside
 * React (event handlers, timers, Office.js callbacks) and rejected promises
 * that nobody handled. Both are currently invisible.
 */
export function installGlobalErrorHandlers(): void {
  try {
    // Send anything still queued before the WebView is torn down. Office
    // recycles the task pane freely, and without this a report queued in the
    // last couple of seconds never leaves. The local copy survives either way.
    const flushOnUnload = () => {
      onUnloadFlush?.();
    };
    window.addEventListener("pagehide", flushOnUnload);
    window.addEventListener("beforeunload", flushOnUnload);

    window.addEventListener("error", (event: ErrorEvent) => {
      reportError(event.error ?? event.message, { area: "window.onerror" });
    });

    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      reportError(event.reason, { area: "unhandledrejection" });
    });
  } catch {
    // Non-browser environments (tests) — nothing to install.
  }
}
